/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/accounts/AccountContractsSection.tsx
 * PURPOSE: AccountDrawer contracts list — create, status, renewal proximity (contracts and renewals)
 * PUBLIC_API: AccountContractsSection
 */

import { FileText, Plus } from 'lucide-react';
import { useState, type FormEvent, type ReactElement } from 'react';
import { twMerge } from 'tailwind-merge';
import type { CrmContract } from '@shared/lib/frontend/api/crmContractsApi';
import { useCrmAccountContracts } from '../../hooks/useCrmAccountContracts';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';
import { formatCrmCurrency } from '../../utils/formatCurrency';
import {
  contractRenewalLabel,
  contractRenewalPillClass,
  contractRenewalTone,
} from '../../utils/contractRenewalLabel';
import { CollapsibleSection } from '../contacts/ContactDrawerBodyChrome';
import { EmptyState } from '../shared/EmptyState';
import { StatusBadge } from '../shared/StatusBadge';

export type AccountContractsSectionProps = {
  accountId: string;
  readOnly?: boolean;
};

function ContractRow({
  contract,
  currency,
  locale,
  onActivate,
  busy,
}: {
  contract: CrmContract;
  currency: string;
  locale: string;
  onActivate?: () => void;
  busy: boolean;
}) {
  const renewalLabel = contractRenewalLabel(contract);
  const renewalTone = contractRenewalTone(contract);
  const valueLabel =
    contract.value != null && Number.isFinite(contract.value)
      ? formatCrmCurrency(contract.value, contract.currencyCode || currency, locale)
      : null;

  return (
    <li
      className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-600 dark:bg-gray-900/30"
      data-testid={`account-contract-row-${contract.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{contract.title}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <StatusBadge status={contract.status} size="sm" />
            {renewalLabel ? (
              <span
                className={twMerge(
                  'rounded-full px-2 py-0.5 text-[10px] font-semibold',
                  contractRenewalPillClass(renewalTone)
                )}
                data-testid={`account-contract-renewal-${contract.id}`}
              >
                {renewalLabel}
              </span>
            ) : null}
            {contract.autoRenew ? (
              <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Auto-renew
              </span>
            ) : null}
          </div>
        </div>
        {valueLabel ? (
          <span className="shrink-0 text-sm font-medium tabular-nums text-gray-800 dark:text-gray-200">
            {valueLabel}
          </span>
        ) : null}
      </div>
      {contract.status === 'draft' && onActivate ? (
        <button
          type="button"
          className="mt-2 text-xs font-medium text-primary-600 hover:underline disabled:opacity-50 dark:text-primary-400"
          disabled={busy}
          data-testid={`account-contract-activate-${contract.id}`}
          onClick={onActivate}
        >
          Mark active
        </button>
      ) : null}
    </li>
  );
}

export function AccountContractsSection({
  accountId,
  readOnly = false,
}: AccountContractsSectionProps): ReactElement {
  const { currency, locale } = useTenantCurrency();
  const { contracts, loading, createContract, creating, patchContract, patching } =
    useCrmAccountContracts(accountId);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [value, setValue] = useState('');
  const [renewalDate, setRenewalDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const busy = creating || patching;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      setFormError('Title is required.');
      return;
    }
    setFormError(null);
    try {
      const parsedValue = value.trim() ? Number(value) : undefined;
      await createContract({
        title: trimmed,
        status: 'draft',
        value: parsedValue != null && Number.isFinite(parsedValue) ? parsedValue : undefined,
        currencyCode: currency,
        renewalDateISO: renewalDate ? `${renewalDate}T12:00:00.000Z` : undefined,
      });
      setTitle('');
      setValue('');
      setRenewalDate('');
      setShowForm(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not create contract.');
    }
  };

  return (
    <CollapsibleSection title="Contracts">
      <div className="space-y-3" data-testid="account-section-contracts">
        {!readOnly ? (
          <div className="flex flex-wrap items-center gap-2">
            {!showForm ? (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary-500"
                data-testid="account-contract-add"
                onClick={() => setShowForm(true)}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                Add contract
              </button>
            ) : null}
          </div>
        ) : null}

        {showForm && !readOnly ? (
          <form
            className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-600 dark:bg-gray-900/40"
            data-testid="account-contract-create-form"
            onSubmit={(e) => void handleSubmit(e)}
          >
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
              Title
              <input
                type="text"
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="account-contract-title-input"
              />
            </label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                Value ({currency})
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              </label>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                Renewal date
                <input
                  type="date"
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
                  value={renewalDate}
                  onChange={(e) => setRenewalDate(e.target.value)}
                />
              </label>
            </div>
            {formError ? <p className="mt-2 text-xs text-red-600 dark:text-red-400">{formError}</p> : null}
            <div className="mt-3 flex gap-2">
              <button
                type="submit"
                disabled={busy}
                className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                data-testid="account-contract-save"
              >
                {busy ? 'Saving…' : 'Save draft'}
              </button>
              <button
                type="button"
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 dark:border-gray-600 dark:text-gray-300"
                onClick={() => {
                  setShowForm(false);
                  setFormError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {loading ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">Loading contracts…</p>
        ) : contracts.length === 0 ? (
          <EmptyState
            title="No contracts yet"
            description="Track agreements, renewal dates, and contract value for this account."
            icon={<FileText className="h-10 w-10 text-gray-300 dark:text-gray-600" aria-hidden />}
          />
        ) : (
          <ul className="space-y-2">
            {contracts.map((c) => (
              <ContractRow
                key={c.id}
                contract={c}
                currency={currency}
                locale={locale}
                busy={busy}
                onActivate={
                  readOnly
                    ? undefined
                    : () => void patchContract({ id: c.id, patch: { status: 'active' } })
                }
              />
            ))}
          </ul>
        )}
      </div>
    </CollapsibleSection>
  );
}
